import { capitalize } from 'lodash'
import {
  BadgeCheck,
  CheckCircle,
  CircleAlert,
  ClockAlert,
  Info,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { DonationDetailDialog } from '../components/DonationDetailDialog'
import { DonationListCard } from '../components/DonationListCard'
import { useDonationsStore } from '../store/donationsStore'
import { PendingDonationForm } from '../components/PendingDonationForm'
import type { donationFilterSchema } from '@/schemas/filterSchemas'
import { Badge } from '@/components/custom/badge'
import { ListPageHeader } from '@/components/custom/ListPageHeader'
import { PaginationWrapper } from '@/components/custom/PaginationWrapper'
import { TBody, TD, TH, THead, TR, Table } from '@/components/custom/table'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { useMembersStore } from '@/features/members/store/membersStore'
import { formatCurrency } from '@/utils/currencyUtils'
import { formatDate, parseLocalDateString } from '@/utils/timeZoneUtils'
import { usePermissions } from '@/utils/permissions'
import { useAuthStore } from '@/features/auth/store/authStore'
import { useCleanQueryStates, usePagination } from '@/hooks'
import { UserRoleEnum } from '@/features/users/types/user'

export default function Donations() {
  const donations = useDonationsStore((s) => s.donations)
  const churches = useChurchesStore((s) => s.churches)
  const members = useMembersStore((s) => s.members)
  const user = useAuthStore((state) => state.user)
  const activeRole = useAuthStore((state) => state.activeRole)
  const permissions = usePermissions()

  const [query, setQuery] =
    useCleanQueryStates<typeof donationFilterSchema>('/authed/donations')
  const [open, setOpen] = useState({
    add: false,
    edit: false,
    delete: false,
    pending: false,
    detail: false,
  })
  const [donationId, setDonationId] = useState<string | null>(null)

  const filtered = useMemo(
    () =>
      donations
        .filter((d) => {
          if (d.isRegistrationFee) return false

          const donor = members.find((mem) => mem._id === d.donorId)

          const matchSearch =
            query.search === '' ||
            donor?.name.toLowerCase().includes(query.search.toLowerCase()) ||
            d.receiptNumber.toLowerCase().includes(query.search.toLowerCase())

          const matchMember =
            query.memberId.length === 0 ||
            (d.donorId && query.memberId.includes(d.donorId))

          const matchChurch =
            query.churchId.length === 0 ||
            (d.churchId && query.churchId.includes(d.churchId))

          const matchMethod =
            query.methods.length === 0 || query.methods.includes(d.method)

          // Fixed date range logic
          const donationDate = new Date(d.date)
          const startDate = query.startDate
            ? parseLocalDateString(query.startDate)
            : null
          const endDate = query.endDate
            ? parseLocalDateString(query.endDate)
            : null

          const matchRange =
            (!startDate || donationDate >= startDate) &&
            (!endDate || donationDate <= endDate)

          const matchStatus = !query.status || query.status === d.status

          const matchActive =
            !query.onlyActives || donor?.active === query.onlyActives

          return (
            matchSearch &&
            matchChurch &&
            matchMember &&
            matchMethod &&
            matchRange &&
            matchStatus &&
            matchActive
          )
        })
        .sort((a, b) => b.receiptNumber.localeCompare(a.receiptNumber)),
    [donations, members, query, user, activeRole],
  )

  const { page, pageCount, rows, setPage, goToFirstPage } = usePagination({
    data: filtered,
    pageSize: 10,
  })

  return (
    <div className="space-y-3">
      <ListPageHeader
        type="donations"
        searchValue={query.search}
        onSearchChange={(value) => {
          setQuery({ search: value })
          goToFirstPage()
        }}
        initialFilters={query}
        onFilter={(filters) => {
          setQuery(filters)
          goToFirstPage()
        }}
        isFormOpen={open.add}
        onFormOpen={() => setOpen((o) => ({ ...o, add: true }))}
        onFormClose={() => setOpen((o) => ({ ...o, add: false }))}
        showAddButton={permissions.canCreateDonation()}
      />

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-hidden rounded-md border bg-white">
        <Table>
          <THead>
            <TR>
              <TH>Donation</TH>
              <TH>Date</TH>
              <TH className="text-right">Amount</TH>
              <TH>Church</TH>
              <TH>Donor</TH>
              <TH>Method</TH>
              <TH>Reference #</TH>
              <TH>Status</TH>
              <TH className="text-right">Actions</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((donation) => {
              const donor = members.find((m) => m._id === donation.donorId)
              const church = churches.find((c) => c._id === donation.churchId)
              return (
                <TR key={donation._id}>
                  <TD>{donation.receiptNumber}</TD>
                  <TD>{formatDate(donation.date, 'MMM D, YYYY')}</TD>
                  <TD className="text-right">
                    {formatCurrency(donation.amount)}
                  </TD>
                  <TD>
                    {donation.churchId && church
                      ? church.name
                      : 'Unassigned Church'}
                  </TD>
                  <TD>
                    {donation.donorId && donor
                      ? donor.name
                      : 'Unassigned Donor'}
                  </TD>
                  <TD>{capitalize(donation.method)}</TD>
                  <TD>{donation.donationRef}</TD>
                  <TD>
                    <Badge
                      color={
                        donation.status === 'verified' ? 'green' : 'yellow'
                      }
                      icon={
                        donation.status === 'verified' ? BadgeCheck : ClockAlert
                      }
                    >
                      {capitalize(donation.status)}
                    </Badge>
                    {donation.method === 'cash' && !donation.deposited && (
                      <Badge className="ml-2" color="red" icon={CircleAlert}>
                        Not Deposited
                      </Badge>
                    )}
                  </TD>
                  <TD className="text-right">
                    <div className="flex justify-end gap-2">
                      {donation.status === 'pending' &&
                        ((donation.churchId &&
                          permissions.canVerifyDonation(donation.churchId)) ||
                          activeRole === UserRoleEnum.SYSTEM_ADMIN) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setDonationId(donation._id)
                              setOpen({ ...open, pending: true })
                            }}
                          >
                            <CheckCircle className="h-4 w-4" /> Verify
                          </Button>
                        )}

                      {donation.status === 'verified' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setDonationId(donation._id)
                            setOpen({ ...open, detail: true })
                          }}
                        >
                          <Info className="h-4 w-4" /> Details
                        </Button>
                      )}
                    </div>
                  </TD>
                </TR>
              )
            })}
            {rows.length === 0 && (
              <TR>
                <TD className="p-6 text-center text-neutral-500" colSpan={8}>
                  No results
                </TD>
              </TR>
            )}
          </TBody>
        </Table>
      </div>

      {/* Mobile Cards */}
      <ScrollArea className="lg:hidden h-[calc(100vh-170px)]">
        <div className="space-y-3">
          {filtered.length === 0 ? (
            <div className="bg-white rounded-lg border p-6 text-center text-neutral-500">
              No donations found
            </div>
          ) : (
            filtered.map((donation) => {
              const donor = members.find((m) => m._id === donation.donorId)
              const church = churches.find((c) => c._id === donation.churchId)
              const donorName = donor ? donor.name : 'Unassigned Donor'
              const churchName = church ? church.name : 'Unassigned Church'

              return (
                <DonationListCard
                  key={donation._id}
                  donation={donation}
                  donorName={donorName}
                  churchName={churchName}
                  permissions={permissions}
                  open={open}
                  donationId={donationId}
                  onEdit={(id) => {
                    setDonationId(id)
                    setOpen({ ...open, edit: true })
                  }}
                  onDelete={(id) => {
                    setDonationId(id)
                    setOpen({ ...open, delete: true })
                  }}
                  onPending={(id) => {
                    setDonationId(id)
                    setOpen({ ...open, pending: true })
                  }}
                  onDetail={(id) => {
                    setDonationId(id)
                    setOpen({ ...open, detail: true })
                  }}
                  onClose={() => {
                    setDonationId(null)
                    setOpen({
                      ...open,
                      edit: false,
                      delete: false,
                      detail: false,
                    })
                  }}
                />
              )
            })
          )}
        </div>
      </ScrollArea>

      {/* Desktop Pagination */}
      <div className="hidden lg:block">
        <PaginationWrapper
          pageCount={pageCount}
          currentPage={page}
          onPageChange={setPage}
        />
      </div>

      <PendingDonationForm
        id={donationId}
        open={open.pending}
        onClose={() => {
          setOpen({ ...open, pending: false })
          setDonationId(null)
        }}
      />

      <DonationDetailDialog
        donationId={donationId!}
        onClose={() => {
          setDonationId(null)
          setOpen({ ...open, detail: false })
        }}
        open={open.detail}
      />
    </div>
  )
}
